/**
 * AgentsSection - Core Reusable Agents Display Component
 * 
 * A sophisticated section for showcasing real estate agents with modern typography
 * and image presentation. The agent's name appears as large background text.
 * 
 * This is a SCORABLE component - theme-agnostic and reusable across all themes.
 * 
 * Features:
 * - Agent name as large typographic element
 * - Clean, modern layout
 * - Responsive grid
 * - Customizable via renderAgent prop
 */

'use client';

import { motion } from 'framer-motion';
import React from 'react';

export interface Agent {
  id: string;
  name: string;
  image: string;
  title?: string;
  bio?: string;
  phone?: string;
  email?: string;
  specialties?: string[];
  properties?: number;
  rating?: number;
}

export interface AgentsSectionProps {
  /**
   * Array of agents to display
   */
  agents: Agent[];
  
  /**
   * Section title
   */
  title?: string;
  
  /**
   * Section subtitle
   */
  subtitle?: string;
  
  /**
   * Number of columns in the grid
   * @default 3
   */
  columns?: 1 | 2 | 3 | 4;
  
  /**
   * Custom className for the section
   */
  className?: string;
  
  /**
   * Custom render function for each agent card
   */
  renderAgent?: (agent: Agent, index: number) => React.ReactNode;
  
  /**
   * Enable/disable animations
   * @default true
   */
  animated?: boolean;
  
  /**
   * Gap between grid items
   * @default 'lg'
   */
  gap?: 'sm' | 'md' | 'lg' | 'xl';
}

const gapClasses = {
  sm: 'gap-4',
  md: 'gap-6',
  lg: 'gap-8',
  xl: 'gap-8 lg:gap-12',
};

const columnClasses = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 md:grid-cols-2',
  3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
};

/**
 * Core Agents Section Component
 */
export function AgentsSection({
  agents,
  title,
  subtitle,
  columns = 3,
  className = '',
  renderAgent,
  animated = true,
  gap = 'lg',
}: AgentsSectionProps) {
  const gridClasses = `
    grid
    ${columnClasses[columns]}
    ${gapClasses[gap]}
  `.trim();

  return (
    <section className={`agents-section ${className}`}>
      {/* Section Header */}
      {(title || subtitle) && (
        <div className="agents-section-header">
          {title && <h2 className="agents-section-title">{title}</h2>}
          {subtitle && <p className="agents-section-subtitle">{subtitle}</p>}
        </div>
      )}

      {/* Agents Grid */}
      <div className={gridClasses}>
        {agents.map((agent, index) => {
          const content = renderAgent 
            ? renderAgent(agent, index)
            : <DefaultAgentCard agent={agent} />;

          if (!animated) {
            return <div key={agent.id}>{content}</div>;
          }

          return (
            <motion.div
              key={agent.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.15,
                ease: [0.25, 0.46, 0.45, 0.94] 
              }}
            >
              {content}
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Default Agent Card
 * Simple card used when no custom renderAgent is provided
 * Note: Themes should use next/image in their custom renderAgent implementations
 */
function DefaultAgentCard({ agent }: { agent: Agent }) {
  return (
    <div className="agent-card">
      <div className="agent-card-image">
        <div className="w-full h-full bg-gray-200" />
      </div>
      <div className="agent-card-content">
        <h3 className="agent-card-name">{agent.name}</h3>
        {agent.title && <p className="agent-card-title">{agent.title}</p>}
        {agent.bio && <p className="agent-card-bio">{agent.bio}</p>}
      </div>
    </div>
  );
}

